import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface Message {
  id: string;
  role: "user" | "bot";
  text: string;
}

const BOT_RESPONSES: Record<string, string> = {
  "bugün ne yapabilirim": "Bugün için harika önerilerim var! 🌿 Sabah Ulu Cami ve Hanlar Bölgesi ile tarihi bir başlangıç yapın. Öğle yemeği için Kebapçı İskender'e uğrayın. Öğleden sonra Teleferik ile Uludağ'a çıkın veya Extrempark'ta macera yaşayın. Akşam Çekirge'de termal bir dinlenme.",
  "what can i do today": "Great picks for today! 🌿 Start the morning at Ulu Cami, grab lunch at Kebapçı İskender, then take the cable car up to Uludag or visit Extrempark for adventure. End the evening relaxing in Cekirge thermal baths.",
  "tarihi yerler": "Bursa'nın en iyi tarihi mekanları: 🏛️ Ulu Cami (20 kubbeli görkemli cami), Yeşil Cami & Türbe (türkuaz çiniler), Osman Gazi Türbesi (imparatorluğun başlangıcı), Cumalıkızık (700 yıllık Osmanlı köyü), Hanlar Bölgesi (tarihi çarşılar). Hepsini 'Tarihi Yerler' kategorisinde bulabilirsiniz.",
  "best historical sites": "Top historical sites in Bursa: 🏛️ Ulu Cami (20-domed grand mosque), Yesil Mosque & Mausoleum (turquoise tiles), Osman Gazi Tomb, Cumalikizik village (700-year-old Ottoman), and the Grand Bazaar area.",
  "rota öner": "Size uygun rotayı seçelim! 🗺️\n\n• ⏱️ 1 Günlük: Ulu Cami → İskender → Yeşil Türbe → Teleferik\n• 🍽️ Lezzet Turu: Cumalıkızık Kahvaltısı → Kapalı Çarşı → İskender → Extrempark Terası\n• 👨‍👩‍👧 Aile: Botanik Park → Teleferik → Extrempark → Akşam yemeği\n• 🏛️ Tarih Turu: Osman Gazi → Ulu Cami → Hanlar → Cumalıkızık\n\nHangisi ilginizi çekiyor?",
  "suggest a route": "Let me suggest the perfect route! 🗺️\n\n• ⏱️ 1 Day: Ulu Cami → Iskender → Green Mosque → Cable Car\n• 🍽️ Food Tour: Cumalikizik Breakfast → Bazaar → Iskender → Extrempark Terrace\n• 👨‍👩‍👧 Family: Botanical Park → Cable Car → Extrempark → Dinner\n\nWhich one interests you?",
  "çocuklarla": "Çocuklarla Bursa'da yapılacaklar: 🎉 Extrempark Bursa (dağ kızağı, ipli parkurlar, çocuk aktiviteleri), Teleferik (panoramik yolculuk), Botanik Park (yürüyüş ve piknik), Cumalıkızık (tarih dersi gibi köy gezisi), Hayvanat Bahçesi. Extrempark en popüler seçenek!",
  "with kids": "Best places with kids in Bursa: 🎉 Extrempark (mountain sled, rope courses, kids activities), Cable Car (scenic ride), Botanical Garden (walking & picnic), Cumalikizik (fun historic village). Extrempark is the most popular!",
  "restoranlar": "Bursa'nın en iyi restoranları: 🍽️ Kebapçı İskender (1867'den beri, orijinal tarif), İskenderoğlu (geleneksel lezzet), Hacı Bey Restaurant (modern ortam), Extrempark Seyir Terası (manzara eşliğinde kahve), Çekirge'deki termal otellerin restoranları. İskender yemeden gitmeyin!",
  "best restaurants": "Top restaurants in Bursa: 🍽️ Kebapçı İskender (original since 1867), Iskenderoglu (traditional), Haci Bey (modern setting), Extrempark Viewing Terrace (coffee with views). Don't leave without trying Iskender!",
};

function getBotResponse(input: string): string {
  const lower = input.toLowerCase().trim();
  for (const [key, response] of Object.entries(BOT_RESPONSES)) {
    if (lower.includes(key)) return response;
  }
  return "Bursa'da keşfedilecek çok şey var! Tarihi yerler, lezzet durakları, doğa rotaları veya macera için 'Keşfet' butonuna tıklayabilir ya da yukarıdaki hızlı önerilerden birini seçebilirsiniz. 🌿";
}

export default function ChatbotWidget() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: "greeting", role: "bot", text: t("chatbot_greeting") },
  ]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = (text: string = input) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const userMsg: Message = { id: Date.now().toString(), role: "user", text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    setTimeout(() => {
      const botReply = getBotResponse(trimmed);
      setMessages((prev) => [
        ...prev,
        { id: Date.now().toString() + "_bot", role: "bot", text: botReply },
      ]);
    }, 700);
  };

  const suggestions = [
    t("chatbot_suggestion_1"),
    t("chatbot_suggestion_2"),
    t("chatbot_suggestion_3"),
    t("chatbot_suggestion_4"),
    t("chatbot_suggestion_5"),
  ];

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-2xl bg-anthracite-900 hover:bg-anthracite-700 text-white shadow-2xl flex items-center justify-center transition-all cursor-pointer"
        title={t("chatbot_title")}
      >
        {isOpen ? (
          <i className="ri-close-line text-xl" />
        ) : (
          <i className="ri-robot-2-line text-xl" />
        )}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-24 left-6 z-50 w-80 md:w-96 bg-white rounded-2xl border border-stone-200 overflow-hidden animate-fade-up flex flex-col" style={{ maxHeight: "520px" }}>
          {/* Header */}
          <div className="bg-anthracite-900 px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-forest-600 flex items-center justify-center">
              <i className="ri-robot-2-line text-white text-base" />
            </div>
            <div>
              <h4 className="text-white font-semibold font-body text-sm">{t("chatbot_title")}</h4>
              <p className="text-white/50 text-xs font-body">{t("chatbot_subtitle")}</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="ml-auto text-white/40 hover:text-white cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 bg-stone-50" style={{ minHeight: "200px" }}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-xs font-body leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-anthracite-900 text-white rounded-br-sm"
                      : "bg-white text-anthracite-700 border border-stone-200 rounded-bl-sm"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 bg-stone-50 border-t border-stone-100 flex flex-wrap gap-1.5">
              {suggestions.slice(0, 3).map((sug) => (
                <button
                  key={sug}
                  onClick={() => handleSend(sug)}
                  className="px-3 py-1.5 rounded-full bg-white border border-stone-200 text-xs text-anthracite-600 hover:border-forest-400 hover:text-forest-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {sug}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 bg-white border-t border-stone-100 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder={t("chatbot_placeholder")}
              className="flex-1 px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-xs font-body text-anthracite-700 placeholder:text-stone-400 outline-none focus:border-forest-400 transition-colors"
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim()}
              className="px-4 py-2 bg-forest-600 disabled:bg-stone-200 hover:bg-forest-500 text-white rounded-xl transition-colors cursor-pointer disabled:cursor-not-allowed text-xs font-semibold font-body whitespace-nowrap"
            >
              <i className="ri-send-plane-fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
